import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';
import { format, parseISO } from 'date-fns';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ReferenceLine,
} from 'recharts';

export const PaymentFeesPercentChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance } = useBoardChartData();

    const chartData = useMemo(() => {
        if (!finance?.dailyBreakdown) return [];

        const sortedDates = Object.keys(finance.dailyBreakdown!).sort();

        return sortedDates
            .map((date) => {
                const metrics = finance.dailyBreakdown![date]?.metrics || {};
                const fees = Math.abs(Number(metrics.transactionFees) || 0);
                const revenue = Number(metrics.revenue) || 0;
                if (revenue === 0) return null;

                return {
                    date,
                    formattedDate: format(parseISO(date), 'MMM dd'),
                    feePercent: Math.round((fees / revenue) * 10000) / 100,
                };
            })
            .filter(Boolean) as { date: string; formattedDate: string; feePercent: number }[];
    }, [finance?.dailyBreakdown]);

    const isEmpty = useMemo(() => chartData.length === 0, [chartData]);

    useSetChartMetadata({ isEmpty });

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        data={chartData}
                        margin={CHART_STYLES.margin.withAxis}
                    >
                        <CartesianGrid
                            vertical={false}
                            stroke={CHART_STYLES.grid.stroke}
                            strokeDasharray={CHART_STYLES.grid.strokeDasharray}
                            strokeWidth={CHART_STYLES.grid.strokeWidth}
                            opacity={CHART_STYLES.grid.opacity}
                        />
                        <XAxis
                            dataKey="formattedDate"
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickMargin={CHART_STYLES.axis.tickMargin}
                        />
                        <YAxis
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickFormatter={(value: number) => `${value}%`}
                        />
                        <Tooltip
                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                            cursor={CHART_STYLES.tooltip.cursor}
                            formatter={(value: number) => [
                                `${value}%`,
                                t('charts.feePercentOfRevenue', 'Fee % of Revenue'),
                            ]}
                            labelFormatter={(label: string) => label}
                        />
                        <ReferenceLine
                            y={3}
                            stroke={CHART_COLORS.danger}
                            strokeDasharray="4 4"
                            label={{
                                value: '3%',
                                position: 'insideTopRight',
                                style: { fill: CHART_COLORS.danger, fontSize: 10 },
                            }}
                        />
                        <Line
                            type="monotone"
                            dataKey="feePercent"
                            stroke={CHART_COLORS.chart.line4}
                            strokeWidth={CHART_STYLES.line.strokeWidth}
                            dot={CHART_STYLES.line.dot}
                            activeDot={{ ...CHART_STYLES.line.activeDot, fill: CHART_COLORS.chart.line4 }}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
